import { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as api from "../api/endpoints";
import { clearToken, getToken, setToken } from "../api/authStorage";
import { ApiError, type UserOut } from "../api/types";

type AuthState = {
  user: UserOut | null;
  loading: boolean;
  login: (login: string, password: string) => Promise<UserOut>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserOut | null>(null);
  const [loading, setLoading] = useState<boolean>(() => !!getToken());

  // на маунте — если есть токен, тянем профиль
  useEffect(() => {
    const token = getToken();
    if (!token) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- инициализация при отсутствии токена, без cascading-эффектов
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.me({ skipAuthRedirect: true });
        if (!cancelled) setUser(res.user);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          clearToken();
        }
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const doLogin = useCallback(async (loginVal: string, password: string) => {
    const res = await api.login({ login: loginVal, password });
    setToken(res.token);
    setUser(res.user);
    return res.user;
  }, []);

  const doLogout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignore — JWT stateless, главное токен очистить локально */
    } finally {
      clearToken();
      setUser(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    try {
      const res = await api.me({ skipAuthRedirect: true });
      setUser(res.user);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearToken();
        setUser(null);
      }
    }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login: doLogin, logout: doLogout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Карта ролей бэкенда → дефолтная стартовая страница.
// eslint-disable-next-line react-refresh/only-export-components
export function pageBaseForRole(role: string): string {
  switch (role) {
    case "admin":
      return "/admin";
    case "dev":
      return "/dev";
    case "local_admin":
      return "/local";
    case "user":
      return "/client";
    default:
      return "/admin";
  }
}

// Человекочитаемый ярлык роли (для меню и шапки).
// eslint-disable-next-line react-refresh/only-export-components
export function roleLabel(role: string): string {
  switch (role) {
    case "admin":
      return "Глоб. админ";
    case "dev":
      return "Разработчик";
    case "local_admin":
      return "Лок. админ";
    case "user":
      return "Пользователь";
    default:
      return role;
  }
}
